// Phase 6 — Deterministic scoring engines for the Freight Intelligence layer.
// Every score returns a numeric value 0–100 and an explanation payload (reasons + weights).
// Consumed by the booking wizard, orchestration dashboard and (later) automation jobs.
import type { MipeResult } from "./pricingV2";
import type { RankedCarrier } from "./matching";
import type { RiskResult } from "./risk";

export interface ScoreExplanation {
  score: number;
  label: "excellent" | "good" | "fair" | "poor";
  reasons: string[];
  factors: Record<string, number>;
}

const label = (s: number): ScoreExplanation["label"] =>
  s >= 85 ? "excellent" : s >= 70 ? "good" : s >= 50 ? "fair" : "poor";

// -------------------------------------------------------------------------
// Readiness — is the load ready to dispatch? (docs, terms, contact, cargo)
// -------------------------------------------------------------------------
export function readinessScore(i: {
  termsAccepted: boolean;
  hasPickupDate: boolean;
  hasDeliveryDeadline: boolean;
  cargoDescribed: boolean;
  declaredValue: number;
  insuranceRequired: boolean;
  vehicleSelected: boolean;
}): ScoreExplanation {
  const factors = {
    terms: i.termsAccepted ? 20 : 0,
    pickup: i.hasPickupDate ? 15 : 0,
    delivery: i.hasDeliveryDeadline ? 10 : 5,
    cargo: i.cargoDescribed ? 20 : 0,
    value: i.declaredValue > 0 ? 15 : 0,
    insurance: (!i.insuranceRequired || i.declaredValue > 0) ? 10 : 0,
    vehicle: i.vehicleSelected ? 10 : 0,
  };
  const score = Object.values(factors).reduce((s, v) => s + v, 0);
  const reasons: string[] = [];
  if (!i.termsAccepted) reasons.push("Shipper terms not accepted yet.");
  if (!i.hasPickupDate) reasons.push("Pickup date missing.");
  if (!i.cargoDescribed) reasons.push("Cargo description incomplete.");
  if (i.insuranceRequired && i.declaredValue === 0) reasons.push("Insurance requested but declared value is 0.");
  return { score, label: label(score), reasons, factors };
}

// -------------------------------------------------------------------------
// Acceptance likelihood — probability the best-ranked carrier will accept.
// -------------------------------------------------------------------------
export function acceptanceScore(top: RankedCarrier | undefined, risk: RiskResult): ScoreExplanation {
  if (!top) return { score: 0, label: "poor", reasons: ["No matching carrier available."], factors: {} };
  const carrierAcceptance = Math.min(100, top.carrier.acceptanceRate);
  const matchQuality = top.score;
  const riskPenalty = Math.min(30, risk.score * 0.3);
  const raw = carrierAcceptance * 0.5 + matchQuality * 0.4 - riskPenalty * 0.1;
  const score = Math.max(0, Math.min(100, Math.round(raw)));
  const reasons: string[] = [
    `Carrier historical acceptance ${top.carrier.acceptanceRate}%`,
    `Match quality ${top.score}/100`,
  ];
  if (risk.score >= 50) reasons.push("Risk profile may deter acceptance.");
  return { score, label: label(score), reasons, factors: { carrierAcceptance, matchQuality, riskPenalty } };
}

// -------------------------------------------------------------------------
// Margin — commission + expected margin over MIPE cost lines.
// -------------------------------------------------------------------------
export function marginScore(mipe: MipeResult): ScoreExplanation {
  const costLines = mipe.lines.filter(l => ["fuel", "toll", "labour", "maintenance"].includes(l.category)).reduce((s, l) => s + l.amount, 0);
  const revenue = mipe.subtotal;
  const gross = revenue - costLines;
  const grossPct = revenue > 0 ? Math.round((gross / revenue) * 100) : 0;
  const commissionPct = Math.round(mipe.commissionRate * 100);
  const score = Math.max(0, Math.min(100, Math.round(grossPct + commissionPct * 0.6)));
  const reasons = [
    `Cost base R${Math.round(costLines).toLocaleString()} vs revenue R${Math.round(revenue).toLocaleString()}`,
    `Gross margin ${grossPct}% · commission ${commissionPct}%`,
  ];
  return { score, label: label(score), reasons, factors: { grossPct, commissionPct, costLines, revenue } };
}

// -------------------------------------------------------------------------
// Corridor confidence — how well Moveload knows this lane.
// -------------------------------------------------------------------------
export function corridorScore(mipe: MipeResult): ScoreExplanation {
  const hasCorridor = !!mipe.explanation.corridor;
  const hasFuel = !!mipe.explanation.fuel_source;
  const hasVehicle = !!mipe.explanation.vehicle_profile;
  const score = (hasCorridor ? 50 : 20) + (hasFuel ? 25 : 5) + (hasVehicle ? 25 : 10);
  const reasons: string[] = [];
  if (hasCorridor) reasons.push(`Known corridor ${mipe.explanation.corridor}`);
  else reasons.push("Unlisted corridor — using default distance model.");
  if (hasFuel) reasons.push(`Live fuel index (${mipe.explanation.fuel_source})`);
  if (hasVehicle) reasons.push(`Vehicle profile ${mipe.explanation.vehicle_profile}`);
  return { score, label: label(score), reasons, factors: { hasCorridor: hasCorridor ? 1 : 0, hasFuel: hasFuel ? 1 : 0, hasVehicle: hasVehicle ? 1 : 0 } };
}

// -------------------------------------------------------------------------
// Delegation — safe for automated dispatch, or founder-review required?
// -------------------------------------------------------------------------
export function delegationScore(readiness: ScoreExplanation, acceptance: ScoreExplanation, risk: RiskResult, margin: ScoreExplanation): ScoreExplanation {
  const raw = readiness.score * 0.3 + acceptance.score * 0.3 + margin.score * 0.2 + (100 - risk.score) * 0.2;
  const score = Math.max(0, Math.min(100, Math.round(raw)));
  const reasons: string[] = [];
  if (risk.requiresManualApproval) reasons.push("Risk flagged for manual review — auto-dispatch blocked.");
  if (readiness.score < 70) reasons.push("Load not fully ready — cannot delegate.");
  if (acceptance.score < 50) reasons.push("Low acceptance probability — human dispatcher preferred.");
  if (score >= 85 && !risk.requiresManualApproval) reasons.push("Safe for autonomous dispatch.");
  return { score, label: label(score), reasons, factors: { readiness: readiness.score, acceptance: acceptance.score, margin: margin.score, riskInverse: 100 - risk.score } };
}

export interface FreightIntelligence {
  readiness: ScoreExplanation;
  acceptance: ScoreExplanation;
  margin: ScoreExplanation;
  corridor: ScoreExplanation;
  delegation: ScoreExplanation;
  overall: number;
}

export function summarise(scores: Omit<FreightIntelligence, "overall">): number {
  return Math.round(
    scores.readiness.score * 0.15 +
    scores.acceptance.score * 0.25 +
    scores.margin.score * 0.20 +
    scores.corridor.score * 0.15 +
    scores.delegation.score * 0.25
  );
}

export const SCORE_TONE: Record<ScoreExplanation["label"], string> = {
  excellent: "bg-success/15 text-success border-success/30",
  good: "bg-primary/10 text-primary border-primary/30",
  fair: "bg-warning/15 text-warning-foreground border-warning/30",
  poor: "bg-destructive/10 text-destructive border-destructive/30",
};

// -------------------------------------------------------------------------
// CBR v3 variants — pricing truth for road freight comes from the cbr-v3
// engine, so margin/corridor scores are derived from its decomposition.
// -------------------------------------------------------------------------
export interface CbrScoreInput {
  lineItems: Array<{ key: string; amount: number; source?: string }>;
  operatingFloor: number;
  cbr: number;
  customerExVat: number;
  commissionRate: number;
  confidence: number;
}

export function marginScoreFromCbr(b: CbrScoreInput): ScoreExplanation {
  const costLines = b.lineItems
    .filter(l => ["fuel", "labour", "maintenance", "standing", "tolls"].includes(l.key))
    .reduce((s, l) => s + l.amount, 0);
  const revenue = b.customerExVat;
  const grossPct = revenue > 0 ? Math.round(((revenue - costLines) / revenue) * 100) : 0;
  const commissionPct = Math.round(b.commissionRate * 100);
  const score = Math.max(0, Math.min(100, Math.round(grossPct + commissionPct * 0.6)));
  return {
    score,
    label: label(score),
    reasons: [
      `Operating cost base R${Math.round(costLines).toLocaleString()} vs customer ex-VAT R${Math.round(revenue).toLocaleString()}`,
      `Gross margin ${grossPct}% · Moveload commission ${commissionPct}% of CBR`,
    ],
    factors: { grossPct, commissionPct, costLines, revenue, cbr: b.cbr },
  };
}

export function corridorScoreFromCbr(b: CbrScoreInput): ScoreExplanation {
  const hasFuel = b.lineItems.some(l => l.key === "fuel" && !!l.source);
  const hasTolls = b.lineItems.some(l => l.key === "tolls");
  const score = Math.max(0, Math.min(100, Math.round(b.confidence * 0.7 + (hasFuel ? 20 : 5) + (hasTolls ? 10 : 5))));
  const reasons = [`Engine confidence ${b.confidence}/100`];
  reasons.push(hasFuel ? "Live fuel index applied" : "Fallback fuel price used");
  reasons.push(hasTolls ? "Authoritative toll amount included" : "No toll segment on this route");
  return { score, label: label(score), reasons, factors: { confidence: b.confidence, hasFuel: hasFuel ? 1 : 0, hasTolls: hasTolls ? 1 : 0 } };
}
